import "./styles.scss";

import CardCreateDeliveryDetails from "components/card-send-create/card-create-delivery/card-create-delivery-details";
import InputFormField from "components/form/input";
import Button from "components/ui/button";
import Dialog from "components/ui/dialog";
import { apiErrorsToFormikErrors } from "components/utils/form";
import { Formik } from "formik";
import { useQueryDict } from "hooks/app";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { bdayFetch } from "services/utils";
import * as Yup from "yup";

const SignerSchema = Yup.object().shape({
  inviteEmail: Yup.string().email("Signer email is invalid").required("Signer email is required!"),
  inviteName: Yup.string().nullable(),
});

const CardCreateSignerForm = ({ className, cardSend, signer, index, onErrors, onRemove, onChange }) => {
  const { queryDict } = useQueryDict();
  const { cardSendId } = cardSend;
  const { cardSignerId, inviteEmailTrackingId, inviteName, inviteEmail } = signer || {};
  const [showFormFields, setShowFormFields] = useState(!cardSignerId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const onRemoveSigner = async () => {
    const payload = { cardSendId, ...queryDict, signers: [{ cardSignerId }] };
    const { errors, ...result } = await bdayFetch("/api/1/card/send/signers", { method: "DELETE", body: payload });
    if (errors?.length > 0) {
      onErrors(errors);
    } else {
      setDeleteDialogOpen(false);
      onChange(result.cardSend);
    }
  };

  const addSigner = async (values, actions) => {
    const payload = {
      cardSendId,
      signers: [values],
    };
    const { errors, ...result } = await bdayFetch("/api/1/card/send/signers", { method: "POST", body: payload });
    if (errors?.length > 0) {
      actions?.setErrors(apiErrorsToFormikErrors(errors));
    } else {
      onChange(result.cardSend);
      onRemove();
    }
    actions?.setSubmitting(false);
  };

  const updateSigner = async (values, actions) => {
    const payload = {
      cardSendId,
      signers: [{ cardSignerId, ...values }],
    };
    const { errors, ...result } = await bdayFetch("/api/1/card/send/signers", { method: "PUT", body: payload });
    if (errors?.length > 0) {
      actions?.setErrors(apiErrorsToFormikErrors(errors));
    } else {
      onChange(result.cardSend);
      setShowFormFields(false);
    }
    actions?.setSubmitting(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        inviteName: inviteName || "",
        inviteEmail: inviteEmail || "",
      }}
      validationSchema={SignerSchema}
      onSubmit={cardSignerId ? updateSigner : addSigner}
    >
      {({ handleSubmit, handleChange, handleBlur, touched, isSubmitting, values, errors }) => (
        <form onSubmit={handleSubmit} className={`card-create-signer-form-container ${className}`}>
          {!showFormFields ? (
            <CardCreateDeliveryDetails
              index={index + 1}
              title={inviteName}
              subtitle={inviteEmail}
              label={inviteEmailTrackingId ? "INVITED" : undefined}
              onEdit={!inviteEmailTrackingId ? () => setShowFormFields(true) : undefined}
            />
          ) : (
            <div className="form-section-item">
              <div className="form-field-container">
                <InputFormField
                  placeholder={"SIGNER NAME"}
                  value={values.inviteName}
                  id={`inviteName-${index}-${cardSignerId}`}
                  name="inviteName"
                  onKeyDown={(e) => {
                    e.key === "Enter" && e.preventDefault();
                  }}
                  errorMessage={errors.inviteName && touched.inviteName ? errors.inviteName : ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!!inviteEmailTrackingId}
                />
              </div>
              <div className="form-field-container">
                <InputFormField
                  placeholder={"SIGNER EMAIL*"}
                  value={values.inviteEmail}
                  id={`inviteEmail-${index}-${cardSignerId}`}
                  name="inviteEmail"
                  required
                  errorMessage={errors.inviteEmail && touched.inviteEmail ? errors.inviteEmail : ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={!!inviteEmailTrackingId}
                />
              </div>
              <div className="form-actions">
                <Button onClick={() => setDeleteDialogOpen(true)} color="transparent" className="delete-button" disabled={isSubmitting}>
                  REMOVE
                </Button>
                <Button onClick={handleSubmit} className="submit-button" type="submit" disabled={isSubmitting}>
                  {cardSignerId ? "SAVE" : "ADD"}
                </Button>
              </div>
            </div>
          )}
          {showFormFields && errors.general && (
            <div className="form-error-message">
              <span>{errors.general}</span>
            </div>
          )}
          <Dialog open={deleteDialogOpen} title="Are you sure?" onClose={() => setDeleteDialogOpen(false)}>
            <div className="confirm-dialog">
              <p>You are about to remove the signer from the card</p>
              <Button onClick={onRemoveSigner} color="red" style={{ marginBottom: "3rem" }}>
                DELETE
              </Button>
            </div>
          </Dialog>
        </form>
      )}
    </Formik>
  );
};

CardCreateSignerForm.propTypes = {
  className: PropTypes.string,
  cardSend: PropTypes.object.isRequired,
  signer: PropTypes.object,
  index: PropTypes.number,
  onErrors: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.string,
};

CardCreateSignerForm.defaultProps = {
  className: "",
  variant: "",
  onRemove: () => {},
};

export default CardCreateSignerForm;
